import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TableLoaderRow } from "@/components/ui/loader";
import { useAssetDepLedger } from "@/features/finance/api";
import { inr } from "@/features/finance/format";
import { apiList } from "@/integrations/api/client";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";

export const Route = createFileRoute("/_authenticated/finance/depreciation-ledger")({
  component: AssetDepLedgerPage,
});

const BOOK_OPTIONS = [
  { value: "FINANCIAL",  label: "Financial" },
  { value: "INCOME_TAX", label: "Income Tax (IT Act)" },
  { value: "MANAGEMENT", label: "Management" },
];

function useCapitalisedAssets(q: string) {
  return useQuery({
    queryKey: ["capitalised-assets-search", q],
    queryFn: async () => {
      if (!q || q.length < 2) return [];
      const res = await apiList<any>("/api/v1/assets/", { search: q, capitalisation__isnull: false, limit: 20 });
      return (res.results || []).map((a: any) => ({
        id: a.id,
        label: `${a.asset_code} — ${a.name}`,
      }));
    },
  });
}

function AssetDepLedgerPage() {
  const [search, setSearch]       = useState("");
  const [assetId, setAssetId]     = useState<string | null>(null);
  const [assetLabel, setAssetLabel] = useState<string>("");
  const [book, setBook]           = useState("FINANCIAL");
  const [showDropdown, setShowDropdown] = useState(false);

  const { data: suggestions = [] } = useCapitalisedAssets(search);
  const { data, isLoading } = useAssetDepLedger(assetId, book);
  const rows = data ?? [];
  const totalDep = rows.reduce((s, r) => s + r.depreciation_amt, 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Asset Depreciation Ledger</h1>
        <p className="text-sm text-muted-foreground">Full period-by-period depreciation history for any asset.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Select Asset</CardTitle></CardHeader>
        <CardContent className="flex gap-3 items-end flex-wrap">
          <div className="flex-1 min-w-60 relative">
            <p className="text-sm font-medium mb-1">Asset (search by code or name)</p>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="e.g. AST-001 or Monitor…"
                value={assetId ? assetLabel : search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setAssetId(null);
                  setAssetLabel("");
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
              />
            </div>
            {showDropdown && suggestions.length > 0 && !assetId && (
              <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
                {suggestions.map((s: any) => (
                  <button
                    key={s.id}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setAssetId(s.id);
                      setAssetLabel(s.label);
                      setSearch("");
                      setShowDropdown(false);
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Book</p>
            <Select value={book} onValueChange={setBook}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BOOK_OPTIONS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {assetId && (
            <button
              className="text-xs text-muted-foreground underline self-end pb-2"
              onClick={() => { setAssetId(null); setAssetLabel(""); setSearch(""); }}
            >
              Clear
            </button>
          )}
        </CardContent>
      </Card>

      {assetId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {assetLabel} &middot; {book} &middot; {rows.length} entries
              {rows.length > 0 && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  Total depreciated: {inr(totalDep)}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run</TableHead>
                  <TableHead>FY</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Run Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Rate %</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Opening NBV</TableHead>
                  <TableHead className="text-right">Depreciation</TableHead>
                  <TableHead className="text-right">Closing NBV</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableLoaderRow colSpan={10} />}
                {rows.map((r) => (
                  <TableRow key={r.run_id}>
                    <TableCell className="text-sm">{r.run_name || "—"}</TableCell>
                    <TableCell>{r.fy}</TableCell>
                    <TableCell>{r.period}</TableCell>
                    <TableCell>{r.run_date}</TableCell>
                    <TableCell>
                      <Badge variant={r.method === "SLM" ? "secondary" : "outline"}>{r.method}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.dep_rate.toFixed(2)}%</TableCell>
                    <TableCell className="text-right">{r.days_used}</TableCell>
                    <TableCell className="text-right">{inr(r.opening_nbv)}</TableCell>
                    <TableCell className="text-right font-medium text-orange-600 dark:text-orange-400">
                      {inr(r.depreciation_amt)}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{inr(r.closing_nbv)}</TableCell>
                  </TableRow>
                ))}
                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-10">
                      No locked depreciation entries found for this asset.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
